<template>
  <el-card class="fla__card">
    <h2>Episode{{ (episodes.length > 1) ? 's' : '' }}
      <el-button
        type="text"
        @click="addTab"
        icon="el-icon-circle-plus"
      >
        Add more episodes
      </el-button>
    </h2>
    <div class="fla--fast-track__films">
      <el-tabs
        v-model="fastTrackTabName"
        :closable="episodes.length > 1"
        :class="(episodes.length > 1) ? 'multiple' : ''"
        type="card"
        @tab-remove="removeTab"
      >
        <el-tab-pane
          v-for="(item, index) in episodes"
          :key="index"
          :label="item.title || 'Episode ' + (index + 1)"
          :name="item.name"
        >
          <el-form
            ref="form"
            :model="episodes[index]">
            <el-form-item
              :label="'Episode title'"
              prop="title"
              :rules="{
                required: true, validator: $_isEmptyValidator, trigger: 'blur', tabName: episodes[index].name,
              }"
            >
              <el-input 
                v-model="episodes[index].title"
                placeholder="Episode title"
              />
            </el-form-item>

            <el-form-item
              label="Episode description"
              prop="description"
              :rules="{
                required: true, validator: $_isEmptyValidator, trigger: 'blur', tabName: episodes[index].name,
              }"
            >
              <el-input 
                v-model="episodes[index].description"
                placeholder="Episode description"
                />
            </el-form-item>

            <el-form-item
              label="Director"
              class="film-form__collaborator film-form__collaborator--director"
              :class="showContributorOption[index] ? 'has-contributor-option' : ''"
              prop="directors"
              :rules="{
                required: true, validator: $_contributorValidator, trigger: 'editing', tabName: episodes[index].name,
              }"
            >
              <span v-if="showContributorOption[index]" slot="label">
                Director<span v-if="episodes[index].directors.length > 1">s</span>
                &nbsp;
                <el-button
                  icon="el-icon-circle-plus" type="text"
                  @click="$_collaboratorTable_addNewDirector(episodes[index])"
                >Add</el-button>
              </span>
              <CollaboratorTable
                v-model="episodes[index].directors"
                defaultRole="director"
                :showAction="showContributorOption[index]"
                :isTemporary="true"
                :showHeader="false"
                :class="showContributorOption[index] ? 'has-actions' : ''"

                :removeFn="$_collaboratorTable_removeFn"
              />
            </el-form-item>

            <el-form-item
              v-if="showContributorOption[index]"
              class="film-form__collaborator film-form__collaborator--contributors"
              :class="showContributorOption[index] ? 'has-contributor-option' : ''"
              prop="contributors"
              :rules="{
                validator: $_contributorValidator, trigger: 'editing', tabName: episodes[index].name,
              }"
            >
              <span slot="label">
                Contributor<span v-if="episodes[index].contributors.length > 1">s</span>
                &nbsp;
                <el-button
                  icon="el-icon-circle-plus" type="text"
                  @click="$_collaboratorTable_addNewContributor(episodes[index])"
                >Add</el-button>
              </span>
              <CollaboratorTable
                v-model="episodes[index].contributors"
                :showAction="true"
                :isTemporary="true"
                :showHeader="false"
                :enableTyping="false"
                class="has-actions"
              />
            </el-form-item>

            <div
              class="add-more-contributor-button"
              v-if="!showContributorOption[index]"
            >
              <el-button
                size="small"
                @click="$_collaboratorTable_addNewContributor(episodes[index])"
              >
                <i class="el-icon-edit-outline"/> Click here to add additional contributors
              </el-button>
            </div>

            <el-form-item
              prop="video_file_url_alt"
              :label="'Video file URL'"
              :rules="{
                required: true, validator: $_isEmptyValidator, trigger: 'blur', tabName: episodes[index].name,
              }"
              >
              <UrlDropper
                ref="urlDropper"
                :value="{url:episodes[index].video_file_url_alt,password:episodes[index].alt_file_credentials}"
                @input="updateUrl($event,index)"
              />
            </el-form-item>

            <el-form-item
              style="width: 100%"
              label="Genres"
              prop="episode_genres"
              :rules="{
                required: true, validator: $_isEmptyValidator, trigger: 'blur', tabName: episodes[index].name,
              }">
              <el-select
                style="width: 100%"
                :value="episodes[index].episode_genres | unwrap"
                @change="$_updateSelectorData($event, episodes[index], 'episode_genres')"
                multiple
                allow-create
                filterable
                default-first-option
                :placeholder="(editable) ? 'Choose the genres' : 'Not selected'"
                :disabled="!editable"
              >
                <el-option-group
                  v-for="group in genres"
                  :key="group.label"
                  :label="group.label">
                  <el-option
                    v-for="item in group.options"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                    </el-option>
                  </el-option-group>
              </el-select>
            </el-form-item>

            <el-form-item
              style="width: 100%"
              label="Topics"
              prop="episode_topics"
              :rules="{
                required: true, validator: $_isEmptyValidator, trigger: 'blur', tabName: episodes[index].name,
              }">
              <el-select
                style="width: 100%"
                :value="episodes[index].episode_topics | unwrap"
                @change="$_updateSelectorData($event, episodes[index], 'episode_topics')"
                multiple
                allow-create
                filterable
                default-first-option
                :placeholder="(editable) ? 'Add topics' : 'Not selected'"
              >
                <el-option-group
                  v-for="group in topics"
                  :key="group.label"
                  :label="group.label">
                  <el-option
                    v-for="item in group.options"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-option-group>
              </el-select>
            </el-form-item>

            <el-form-item
              prop="country"
              :label="`Countr${
                (episodes[index].country && episodes[index].country.split(',').length > 1)
                  ? 'ies'
                  : 'y'
              }`"
              :rules="{
                required: true, validator: $_isEmptyValidator, trigger: 'blur', tabName: episodes[index].name,
              }"
            >
              <el-select
                style="width: 100%"
                :value="episodes[index].country | unwrap"
                @change="$_updateSelectorData($event, episodes[index], 'country')"
                multiple
                allow-create
                filterable
                default-first-option
                placeholder="Choose the country">
                <el-option-group
                  v-for="group in countries"
                  :key="group.label"
                  :label="group.label">
                  <el-option
                    v-for="item in group.options"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-option-group>
              </el-select>
            </el-form-item>

            <el-form-item
              label="Language"
              prop="language"
              :rules="{
                required: true, validator: $_isEmptyValidator, trigger: 'blur', tabName: episodes[index].name,
              }">
              <el-select
                style="width: 100%"
                :value="episodes[index].language | unwrapLanguageCode"
                @change="$_updateSelectorData($event, episodes[index], 'language')"
                multiple
                allow-create
                filterable
                :placeholder="'Choose the language of the film'"
              >
                <el-option
                  v-for="item in langs"
                  :key="item.label"
                  :label="item.label"
                  :value="item.label"
                ></el-option>
              </el-select>
            </el-form-item>

          </el-form>
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-card>
</template>

<script>
// Libraries and static data
import selectorDataMaker from '@/utils/selectorDataMaker';
import countriesData from '@/assets/data/countries';
import topicsData from '@/assets/data/viddsee/topics';
import genresData from '@/assets/data/viddsee/genres';
import langCodes from '@/assets/data/ISO693-1.json';
// Components
import formValidationMixin from '@/mixins/formValidationMixin';
import CollaboratorTable from '@/components/Submission/steps/CollaboratorDetails/CollaboratorItem/CollaboratorTable';
import UrlDropper from '../../Submission/steps/VideoUpload/UrlDropper';

// TODO: Need to extract this function out so that collaborator details can make use of this implementation
const ERROR = {
  IS_EMPTY: new Error('This field cannot be empty'),
  NO_FIRST_NAME: new Error('First name is required'),
  NO_LAST_NAME: new Error('Last name is required'),
  NO_ROLE: new Error('Contributor\'s role is required'),
  NO_EMAIL: new Error('Email is required'),
  INVALID_EMAIL: new Error('Email is not valid'),
};
const hasValidEmail = emailString => (emailString || '').split('@').length === 2;

export default {
  mixins: [
    formValidationMixin,
  ],
  components: {
    CollaboratorTable,
    UrlDropper,
  },
  props: {
    value: Object,
    editable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      tabIndex: 1,
      fastTrackTabName: '1',
      episodes: [
        {
          name: '1',
          title: '',
          description: '',
          directors: [{
            editable: true,
            role: 'director',
          }],
          contributors: [],
          episode_genres: '',
          episode_topics: '',
          video_file_url_alt: '',
          alt_file_credentials: '',
          country: '',
          language: '',
        },
      ],
      genres: [{ options: selectorDataMaker.make(genresData) }],
      topics: [{ options: selectorDataMaker.make(topicsData) }],
      countries: [{
        label: 'Type to add new country',
        options: selectorDataMaker.make(countriesData),
      }],
      langs: Object.freeze(Object.keys(langCodes).map(key => ({
        value: key,
        label: langCodes[key].name,
      }))),
    };
  },
  watch: {
    // eslint-disable-next-line func-names
    'value.genres': function(newVal) { // watch it
      for (let i=0; i<this.tabIndex; i+=1) {
        this.episodes[i].episode_genres=newVal;
      }
    },
    // eslint-disable-next-line func-names
    'value.topics': function(newVal) { // watch it
      for (let i=0; i<this.tabIndex; i+=1) {
        this.episodes[i].episode_topics=newVal;
      }
    },
  },
  computed: {
    showContributorOption() {
      // Show contributor option only when there are more than 1 collaborator.
      // This case will happen when there are > 1 director or at least one contributor
      return this.episodes.map(film =>
        (film.directors.length > 1) ||
        (film.contributors.length > 0)
      );
    },
  },
  filters: {
    unwrap(episodes) {
      return episodes ? episodes.split(',') : [];
    },
    unwrapLanguageCode(value) {
      // TODO: Some of the language label contains ','
      return (value ? value.split(',') : [])
        // This is performed for backward compabitility. Old language code will be transform automatically
        .map(langKey =>
          (langCodes[langKey] && langCodes[langKey].name) || // Attempt to match ISO key
          langKey // Return original if failed
        );
    },
  },

  methods: {
    addTab() {
      this.$_addTab();
    },
    removeTab(targetName) {
      // Find the related film to generate film name
      const filmIndex = this.episodes.findIndex(film => film.name === targetName);
      const filmToBeDeleted = this.episodes[filmIndex];
      const filmTitle = (filmToBeDeleted && filmToBeDeleted.title)
        ? `${filmToBeDeleted.title}`
        : `Film ${filmIndex + 1}`;

      // Prompt user to for confirmation
      this.$confirm(
        `Are you sure you want to delete "${filmTitle}"?`,
        'Delete Episode',
        {
          confirmButtonText: 'Delete',
          type: 'warning',
        }
      )
        .then(() => {
          this.$_removeTab(targetName);
        })
        .catch(() => {});
    },

    // Component form methods
    $_updateSelectorData(value, filmObject, prop) {
      this.$set(filmObject, prop, value.join(','));
    },
    // Tabs manipulation
    $_addTab() {
      const newTabName = `${this.tabIndex += 1}`;
      const index = this.tabIndex < 2 ? 0 : this.tabIndex-2;
      this.episodes.push({
        title: `Episode ${this.tabIndex}`,
        name: newTabName,
        directors: [...this.episodes[index].directors],
        contributors: [...this.episodes[index].contributors],
        country: this.episodes[index].country,
        episode_genres: this.episodes[index].episode_genres,
        episode_topics: this.episodes[index].episode_topics,
        language: this.episodes[index].language,
        formData: {},
      });
      this.fastTrackTabName = newTabName;
    },

    $_removeTab(targetName) {
      const tabs = this.episodes;
      let activeName = this.fastTrackTabName;
      this.tabIndex-=1;
      // Prepare to switch to next tab
      if (activeName === targetName) {
        tabs.forEach((tab, index) => {
          if (tab.name === targetName) {
            const nextTab = tabs[index + 1] || tabs[index - 1];
            if (nextTab) {
              activeName = nextTab.name;
            }
          }
        });
      }
      this.$_switchTab(activeName);

      // Remove the tab and its data
      this.episodes = tabs.filter(tab => tab.name !== targetName);
      this.$emit('input', this.episodes);
    },

    $_switchTab(targetName) {
      this.fastTrackTabName = targetName;
    },

    // Collaborator update methods
    $_collaboratorTable_addNewDirector(filmObject) {
      filmObject.directors.push({
        editable: true,
        role: 'director',
      });
    },
    $_collaboratorTable_addNewContributor(filmObject) {
      filmObject.contributors.push({
        editable: true,
      });
    },

    $_collaboratorTable_removeFn(humanId, humans) {
      return new Promise((resolve, reject) => {
        if (humans.length > 1) {
          return resolve();
        } else {
          this.$notify.info({
            title: 'Info',
            message: 'At least 1 director is required per film',
          });
          return reject();
        }
      });
    },

    // TODO: Need to extract this function out so that collaborator details can make use of this implementation
    $_contributorValidator(rule, contributors, callback) {
      if (rule.required && contributors.length < 1) {
        return callback(new Error('Please add in at least 1 row'));
      }

      let error; // undefined
      contributors.every((contributor) => {
        if (!contributor.first_name) {
          error = ERROR.NO_FIRST_NAME;
        } else if (!contributor.last_name) {
          error = ERROR.NO_LAST_NAME;
        } else if (!contributor.role) {
          error = ERROR.NO_ROLE;
        } else if (contributor.role === 'director') {
          if (!contributor.email) {
            error = ERROR.NO_EMAIL;
          } else if (!hasValidEmail(contributor.email)) {
            error = ERROR.INVALID_EMAIL;
          }
        }
        // If no error, return true and continue next checking
        return !error;
      });

      if (error) this.$_switchTab(rule.tabName);
      // If all field are valid, error willl be undefined
      return callback(error);
    },

    $_isEmptyValidator(rule, value, callback) {
      const trimmedValue = (value || '').trim();
      if (!trimmedValue) {
        this.$_switchTab(rule.tabName);
        callback(ERROR.IS_EMPTY);
      }
      callback();
    },
    updateUrl(data, index) {
      if (data.url != null) {
        this.episodes[index].video_file_url_alt = data.url;
        this.$emit('input', this.episodes[index].video_file_url_alt);
      }
      if (data.password != null) {
        this.episodes[index].alt_file_credentials = data.password;
        this.$emit('input', this.episodes[index].alt_file_credentials);
      }
    },

  },

};
</script>

<style lang="scss">
$element-ui-spacing: 22px;
$collaborator-table-complex-border: 1px solid rgba(0,0,0,0.05);
$collaborator-table-complex-border-radius: 4px;
$collaborator-table-complex-animation-duration: 0.5s;

.fla--fast-track__films {
  .el-tabs.el-tabs--top {
    .el-tabs__header {
      display: none;
    }
    &.multiple .el-tabs__header {
      display: block;
    }
  }
}

.add-more-contributor-button {
  opacity: 0.9;
  margin-bottom: $element-ui-spacing;
  text-align: right;
}

// Make form label for table clickable
.el-form-item__label {
  position: relative;
  z-index: 1;
}

// Prevent user from removing director if there is only 1 director
.film-form__collaborator--director .collaborator-table.has-actions .el-table__row:first-child:last-child td:last-child {
  cursor: not-allowed;

  .el-button {
    cursor: not-allowed;
    color: lightgray;
  }
}

// Create complex FilmsForm styling
.film-form__collaborator {
  transition: padding $collaborator-table-complex-animation-duration,
              margin $collaborator-table-complex-animation-duration,
              background-color $collaborator-table-complex-animation-duration;
  border: 1px solid transparent;

  .collaborator-table {
    box-shadow: none;
  }
}
.film-form__collaborator.has-contributor-option {
  padding: $element-ui-spacing;
  padding-bottom: 0;
  margin-bottom: 0;
  border-left: $collaborator-table-complex-border;
  border-right: $collaborator-table-complex-border;

  // For some reason, first-type and last-type doesn't work =/
  &:nth-of-type(2) {
    border-top: $collaborator-table-complex-border;
    border-top-left-radius: $collaborator-table-complex-border-radius;
    border-top-right-radius: $collaborator-table-complex-border-radius;
  }
  &:nth-last-of-type(2) {
    padding-bottom: $element-ui-spacing;
    border-bottom: $collaborator-table-complex-border;
    margin-bottom: $element-ui-spacing;
    border-bottom-right-radius: $collaborator-table-complex-border-radius;
    border-bottom-left-radius: $collaborator-table-complex-border-radius;
  }

  // Fix unwanted scroll behaviour
  @keyframes fadeInScrollTrack {
    0%   { overflow-x: hidden; }
    100% { overflow-x: auto; }
  }
  .el-table__body-wrapper {
    animation-name: fadeInScrollTrack;
    animation-duration: $collaborator-table-complex-animation-duration * 4;
    animation-timing-function: steps-end;
  }


  // Override default color
  &,
  .collaborator-table.has-actions .el-table__row td:last-child {
    background-color: #f9f9f9;
  }

  .collaborator-table {
    .el-table,
    .el-table tr {
      background-color: transparent;
    }
  }
}

// Remove collaborator table default styling for form-film
.collaborator-table {

  // Remove bottom line
  .el-table::before {
    display: none;
  }
  .el-table__row td {
    border-bottom: 0;
  }

  // Remove side padding
  .el-table__row {
    td:first-child {
      .cell {
        padding-left: 0;
      }
    }
    td:last-child {
      .cell {
        padding-right: 0;
      }
    }
  }
}

.half-width-input {
  display:flex;
  flex-direction: column;
  align-items: flex-start;
}
</style>



// WEBPACK FOOTER //
// src/components/Licenses/Others/SeriesEpisodesForm.vue